import { Injectable, inject, signal } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { Subscription } from 'rxjs';

const CHUNK_ERROR_RE =
  /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError/i;

@Injectable({ providedIn: 'root' })
export class NewVersionService {
  private router = inject(Router);
  readonly newVersionAvailable = signal(false);

  private initialized = false;
  private routerSub?: Subscription;
  private windowErrorHandler = (e: ErrorEvent): void => {
    if (CHUNK_ERROR_RE.test(e.message ?? '')) this.flag();
  };
  private rejectionHandler = (e: PromiseRejectionEvent): void => {
    const reason = e.reason as { message?: string } | string | undefined;
    const msg =
      typeof reason === 'string'
        ? reason
        : reason?.message ?? (reason !== undefined ? String(reason) : '');
    if (CHUNK_ERROR_RE.test(msg)) this.flag();
  };
  private reloadFn: () => void = () => window.location.reload();

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        const err = event.error as { message?: string } | string | undefined;
        const msg =
          typeof err === 'string' ? err : err?.message ?? (err !== undefined ? String(err) : '');
        if (CHUNK_ERROR_RE.test(msg)) this.flag();
      }
    });

    window.addEventListener('error', this.windowErrorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  dispose(): void {
    if (!this.initialized) return;
    this.initialized = false;
    this.routerSub?.unsubscribe();
    window.removeEventListener('error', this.windowErrorHandler);
    window.removeEventListener('unhandledrejection', this.rejectionHandler);
  }

  setReloadFn(fn: () => void): void {
    this.reloadFn = fn;
  }

  reload(): void {
    this.reloadFn();
  }

  private flag(): void {
    if (!this.newVersionAvailable()) this.newVersionAvailable.set(true);
  }
}
